import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { useAnchoredPosition, useEscapeKey } from '@/hooks'

export interface DropdownItem {
  id: string
  label: string
  icon?: ReactNode
  onSelect?: () => void
  /** Renders in danger tone and last — destructive items are separated. */
  destructive?: boolean
  disabled?: boolean
  selected?: boolean
  /** Draws a divider above this item. */
  separatorBefore?: boolean
}

export interface DropdownProps {
  trigger: (props: { isOpen: boolean; toggle: () => void }) => ReactNode
  items: DropdownItem[]
  align?: 'start' | 'end'
  /** Header text at the top of the panel — e.g. the label of a filter. */
  heading?: string
  className?: string
  menuClassName?: string
}

/**
 * A menu of actions or a single-select filter.
 *
 * PORTALED, and this comment used to say the opposite: "positioned with plain
 * CSS absolute placement rather than a portal ... if a menu ever needs to escape
 * an `overflow: hidden` ancestor, that is the moment to reach for a portal".
 * That moment arrived. `Table` is `overflow-x-auto` so that a wide table scrolls
 * on mobile, and an absolutely-positioned menu on the LAST ROW did not overflow
 * it — it made the whole table a scroll region, which is what a person sees as
 * "the card suddenly became scrollable". `Card` is `overflow-hidden`, which
 * clipped it outright.
 *
 * The bookkeeping the old note worried about is real and is handled here: the
 * menu is measured against the viewport and flips above the trigger when there
 * is no room below, and it CLOSES on scroll rather than following, because a
 * menu that hovers over the page while its own row scrolls away is worse than
 * one that dismisses.
 */
export function Dropdown({
  trigger,
  items,
  align = 'end',
  heading,
  className,
  menuClassName,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setIsOpen(false), [])

  useEscapeKey(close, isOpen)
  const position = useAnchoredPosition(containerRef, menuRef, {
    isOpen,
    side: 'bottom',
    align,
  })

  // The menu is no longer a DOM child of the trigger, so a single
  // `useClickOutside(containerRef)` would treat clicking the menu's own padding
  // or heading as an outside click and dismiss it before the item fired.
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      close()
    }
    // Capture, so a scroll inside any container closes it -- `scroll` does not
    // bubble, and the menu is anchored to a rect that a scroll invalidates.
    const onScroll = () => close()
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [isOpen, close])

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      {trigger({ isOpen, toggle: () => setIsOpen((v) => !v) })}
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              // Hidden for the frame between mount and measurement, so the menu
              // never flashes in the top-left corner before it is placed.
              visibility: position ? 'visible' : 'hidden',
            }}
            className={cn(
              'z-popover min-w-52 animate-scale-in overflow-hidden rounded-md',
              'border border-border bg-surface-raised py-1 shadow-lg',
              position?.side === 'top' ? 'origin-bottom' : 'origin-top',
              menuClassName,
            )}
          >
            {heading && (
              <p className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wide text-fg-subtle">
                {heading}
              </p>
            )}
            {items.map((item) => (
              <div key={item.id}>
                {item.separatorBefore && <div className="my-1 h-px bg-border" />}
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    item.onSelect?.()
                    close()
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-base transition-colors duration-fast',
                    'disabled:pointer-events-none disabled:opacity-50',
                    item.destructive
                      ? 'text-danger hover:bg-danger-subtle'
                      : 'text-fg hover:bg-surface-muted',
                  )}
                >
                  {item.icon && (
                    <span aria-hidden className="shrink-0 text-fg-subtle [&>svg]:h-4 [&>svg]:w-4">
                      {item.icon}
                    </span>
                  )}
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.selected && <Check aria-hidden className="h-4 w-4 shrink-0 text-brand" />}
                </button>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
